import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ModernWorksheetViewer = ({ worksheet, onClose }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!worksheet) return null;

  // Parse worksheet content into sections
  const parseWorksheetSections = (content) => {
    const sections = content.split(/(?=## Section [A-Z])/g).filter(section => section.trim());
    return sections.map(section => {
      const lines = section.split('\n');
      const title = lines.find(line => line.startsWith('## Section')) || `Section ${sections.indexOf(section) + 1}`;
      return {
        title: title.replace('## ', ''),
        content: section,
        icon: getSectionIcon(title)
      };
    });
  };

  const getSectionIcon = (title) => {
    if (title.includes('Multiple Choice')) return '🔢';
    if (title.includes('True') || title.includes('False')) return '✅';
    if (title.includes('Fill')) return '📝';
    if (title.includes('Short Answer')) return '💭';
    if (title.includes('Creative')) return '🎨';
    if (title.includes('Answer Key')) return '🔑';
    return '📋';
  };

  const sections = parseWorksheetSections(worksheet.content || '');

  const downloadWorksheet = () => {
    const element = document.createElement('a');
    const file = new Blob([worksheet.content], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = worksheet.filename || `worksheet_grade_${worksheet.grade}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className={`fixed inset-0 z-50 ${isFullscreen ? 'bg-white' : 'bg-black/60 backdrop-blur-sm flex items-center justify-center p-4'}`}>
      <div className={`${isFullscreen ? 'w-full h-full' : 'w-full max-w-6xl h-[95vh]'} bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden`}>
        
        {/* Modern Header */}
        <div className="relative bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white p-6">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg')" width="60" height="60"></div>
          
          <div className="relative z-10 flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <span className="text-3xl">🎓</span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold mb-1">Smart Worksheet</h1>
                  <p className="text-white/80 text-lg">Interactive Learning Experience</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30">
                  📚 Grade {worksheet.grade}
                </span>
                <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30">
                  🌍 {worksheet.language || 'English'}
                </span>
                <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30">
                  📄 {sections.length} Sections
                </span>
                <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30">
                  ⏱️ ~{Math.ceil(sections.length * 8)} mins
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all border border-white/30"
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isFullscreen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  )}
                </svg>
              </button>
              
              <button
                onClick={onClose}
                className="p-2 bg-red-500/20 backdrop-blur-sm rounded-xl hover:bg-red-500/30 transition-all border border-red-400/30"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-gray-50/80 backdrop-blur-sm px-6 py-4 border-b border-gray-200/50">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {/* Section Navigation */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600">Sections:</span>
                <div className="flex space-x-1">
                  {sections.map((section, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSection(index)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        currentSection === index
                          ? 'bg-violet-500 text-white shadow-lg'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {section.icon} {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAnswers(!showAnswers)}
                className={`px-4 py-2 rounded-xl font-medium transition-all shadow-lg ${
                  showAnswers
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {showAnswers ? '🙈 Hide Answers' : '👁️ Show Answers'}
              </button>
              
              <button
                onClick={downloadWorksheet}
                className="px-4 py-2 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-all shadow-lg"
              >
                📥 Download
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            {/* Section Header */}
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-6 border-b border-violet-200/50">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-violet-500 text-white rounded-2xl text-2xl">
                  {sections[currentSection]?.icon}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-violet-900">
                    {sections[currentSection]?.title}
                  </h2>
                  <p className="text-violet-600">
                    Section {currentSection + 1} of {sections.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Section Content */}
            <div className="p-8">
              <div className="max-w-4xl mx-auto">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-2xl font-bold text-violet-800 mb-6 flex items-center">
                        <span className="w-8 h-8 bg-violet-500 text-white rounded-lg flex items-center justify-center text-sm mr-3">
                          {currentSection + 1}
                        </span>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xl font-semibold text-purple-700 mb-4 flex items-center">
                        <span className="w-2 h-6 bg-purple-400 rounded-full mr-3"></span>
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                        {children}
                      </p>
                    ),
                    ol: ({ children }) => (
                      <ol className="space-y-4 mb-8">
                        {children}
                      </ol>
                    ),
                    ul: ({ children }) => (
                      <ul className="space-y-3 mb-6">
                        {children}
                      </ul>
                    ),
                    li: ({ children }) => (
                      <li className="bg-white p-4 rounded-xl border border-gray-200 hover:border-violet-300 hover:shadow-md transition-all">
                        <div className="flex items-start space-x-3">
                          <span className="w-6 h-6 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                            •
                          </span>
                          <div className="flex-1 text-gray-700">
                            {children}
                          </div>
                        </div>
                      </li>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold text-violet-800 bg-violet-50 px-1 rounded">
                        {children}
                      </strong>
                    ),
                    hr: () => (
                      <div className="my-8 flex items-center">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-300 to-transparent"></div>
                        <span className="px-4 text-violet-500">✨</span>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-300 to-transparent"></div>
                      </div>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 p-6 my-6 rounded-r-xl">
                        <div className="flex items-start">
                          <span className="text-3xl mr-4 text-blue-500">💡</span>
                          <div className="text-blue-800 italic">
                            {children}
                          </div>
                        </div>
                      </blockquote>
                    )
                  }}
                >
                  {sections[currentSection]?.content || ''}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="bg-gray-50/80 backdrop-blur-sm px-6 py-4 border-t border-gray-200/50">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              {sections.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSection ? 'bg-violet-500 w-8' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
              disabled={currentSection === sections.length - 1}
              className="flex items-center space-x-2 px-4 py-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <span>Next</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernWorksheetViewer;